// ============================================
// RECURSOSHUMANOS.JS — Asistencias, expedientes, nómina
// ============================================
import React, { useState, useEffect, useMemo } from 'react';
import { OPC_DINERO } from './utils/dinero';

const PP = "'Poppins', sans-serif";
const COLOR_PRINCIPAL = '#5c9adb';
const COLOR_OSCURO = '#2f5f9e';
const COLOR_FONDO = '#f3f4f4';
const COLOR_GRIS = '#4e5357';
const COLOR_GRIS_CLARO = '#8a8f95';
const COLOR_VERDE = '#5cd4b0';
const COLOR_ROJO = '#e05c5c';

const KEY_ASIS = 'beep_asistencias';
const KEY_RRHH = 'beep_rrhh';
const KEY_USUARIOS = 'beep_usuarios';

const DOCUMENTOS = ['INE', 'CURP', 'NSS', 'Contrato', 'Comprobante domicilio', 'Carta no antecedentes', 'IMSS'];

function cargarUsuarios() {
  try { return JSON.parse(localStorage.getItem(KEY_USUARIOS) || '[]'); } catch { return []; }
}
function cargar(key, def) {
  try { const r = localStorage.getItem(key); if (r) return JSON.parse(r); } catch {}
  return def;
}
function guardar(key, val) { try { localStorage.setItem(key, JSON.stringify(val)); } catch {} }

function hoyISO() {
  const d = new Date(); d.setHours(0, 0, 0, 0); return d.toISOString().slice(0, 10);
}

function iniciales(nombre) {
  return (nombre || '?').split(' ').filter(Boolean).slice(0, 2).map(p => p[0].toUpperCase()).join('');
}

function RecursosHumanos({ rol, esCelular }) {
  const [tab, setTab] = useState('asistencias');
  const [usuarios] = useState(cargarUsuarios);
  const [asistencias, setAsistencias] = useState(() => cargar(KEY_ASIS, []));
  const [rrhh, setRrhh] = useState(() => cargar(KEY_RRHH, {}));

  useEffect(() => guardar(KEY_ASIS, asistencias), [asistencias]);
  useEffect(() => guardar(KEY_RRHH, rrhh), [rrhh]);

  const tabs = [
    { id: 'asistencias', label: '✅ Asistencias' },
    { id: 'expedientes', label: '🗃️ Expedientes' },
    { id: 'nomina', label: '💵 Nómina' },
  ];

  return (
    <div style={{ fontFamily: PP, display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div>
        <div style={{ fontSize: esCelular ? 20 : 24, fontWeight: 800, color: COLOR_GRIS }}>👥 Recursos Humanos</div>
        <div style={{ fontSize: 12, color: COLOR_GRIS_CLARO, marginTop: 4 }}>{usuarios.length} empleados</div>
      </div>

      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        {tabs.map(t => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            style={{
              padding: '8px 16px', borderRadius: 12,
              background: tab === t.id ? COLOR_PRINCIPAL : '#fff',
              color: tab === t.id ? '#fff' : COLOR_OSCURO,
              border: `1.5px solid ${COLOR_PRINCIPAL}`,
              fontWeight: 700, fontSize: 13, cursor: 'pointer', fontFamily: PP,
            }}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'asistencias' && <Asistencias usuarios={usuarios} asistencias={asistencias} setAsistencias={setAsistencias} esCelular={esCelular} />}
      {tab === 'expedientes' && <Expedientes usuarios={usuarios} rrhh={rrhh} setRrhh={setRrhh} esCelular={esCelular} />}
      {tab === 'nomina' && <Nomina usuarios={usuarios} esCelular={esCelular} />}
    </div>
  );
}

function Asistencias({ usuarios, asistencias, setAsistencias, esCelular }) {
  const fecha = hoyISO();

  const registroDe = (uid) => asistencias.find(a => a.usuarioId === uid && a.fecha === fecha) || { usuarioId: uid, fecha, asistio: false, entrada: '', salida: '', observacion: '' };

  const actualizar = (uid, cambios) => {
    setAsistencias(prev => {
      const idx = prev.findIndex(a => a.usuarioId === uid && a.fecha === fecha);
      if (idx === -1) return [...prev, { usuarioId: uid, fecha, asistio: false, entrada: '', salida: '', observacion: '', ...cambios }];
      return prev.map((a, i) => i === idx ? { ...a, ...cambios } : a);
    });
  };

  const card = { background: '#fff', borderRadius: 12, padding: 14, boxShadow: '0 2px 6px rgba(0,0,0,0.05)' };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div style={{ fontSize: 12, color: COLOR_GRIS_CLARO, fontWeight: 700 }}>HOY · {new Date().toLocaleDateString('es-MX', { weekday: 'long', day: 'numeric', month: 'long' })}</div>
      {usuarios.map(u => {
        const r = registroDe(u.id);
        return (
          <div key={u.id} style={card}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{ width: 36, height: 36, borderRadius: '50%', background: COLOR_PRINCIPAL, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700 }}>
                {iniciales(u.nombre)}
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: COLOR_GRIS }}>{u.nombre}</div>
                <div style={{ fontSize: 11, color: COLOR_GRIS_CLARO }}>{u.rol}</div>
              </div>
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: 12, fontWeight: 700, color: r.asistio ? COLOR_VERDE : COLOR_GRIS_CLARO }}>
                <input type="checkbox" checked={r.asistio} onChange={(e) => actualizar(u.id, { asistio: e.target.checked })} />
                {r.asistio ? 'Presente' : 'Ausente'}
              </label>
            </div>
            {r.asistio && (
              <div style={{ display: 'flex', gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
                <input type="time" value={r.entrada} onChange={(e) => actualizar(u.id, { entrada: e.target.value })} style={{ flex: 1, minWidth: 80, padding: 6, borderRadius: 6, border: '1px solid #d8dde3', fontSize: 12, fontFamily: PP }} placeholder="Entrada" />
                <input type="time" value={r.salida} onChange={(e) => actualizar(u.id, { salida: e.target.value })} style={{ flex: 1, minWidth: 80, padding: 6, borderRadius: 6, border: '1px solid #d8dde3', fontSize: 12, fontFamily: PP }} placeholder="Salida" />
                <input type="text" value={r.observacion} onChange={(e) => actualizar(u.id, { observacion: e.target.value })} placeholder="Observación..." style={{ flex: 2, minWidth: 120, padding: 6, borderRadius: 6, border: '1px solid #d8dde3', fontSize: 12, fontFamily: PP }} />
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function Expedientes({ usuarios, rrhh, setRrhh, esCelular }) {
  const card = { background: '#fff', borderRadius: 12, padding: 14, boxShadow: '0 2px 6px rgba(0,0,0,0.05)' };

  const docDe = (uid, doc) => rrhh[uid]?.[doc] || 'faltante';
  const cambiar = (uid, doc, estado) => {
    setRrhh(prev => ({ ...prev, [uid]: { ...(prev[uid] || {}), [doc]: estado } }));
  };

  const iconoEstado = (e) => e === 'completo' ? '✅' : e === 'por_vencer' ? '⚠️' : '❌';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {usuarios.map(u => (
        <div key={u.id} style={card}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
            <div style={{ width: 36, height: 36, borderRadius: '50%', background: COLOR_PRINCIPAL, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700 }}>
              {iniciales(u.nombre)}
            </div>
            <div>
              <div style={{ fontSize: 13, fontWeight: 700, color: COLOR_GRIS }}>{u.nombre}</div>
              <div style={{ fontSize: 11, color: COLOR_GRIS_CLARO }}>{u.rol}</div>
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 6 }}>
            {DOCUMENTOS.map(doc => {
              const e = docDe(u.id, doc);
              return (
                <div key={doc} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 8px', background: COLOR_FONDO, borderRadius: 8 }}>
                  <span>{iconoEstado(e)}</span>
                  <span style={{ flex: 1, fontSize: 11, color: COLOR_GRIS }}>{doc}</span>
                  <select
                    value={e}
                    onChange={(ev) => cambiar(u.id, doc, ev.target.value)}
                    style={{ fontSize: 10, padding: '2px 4px', border: '1px solid #d8dde3', borderRadius: 4, fontFamily: PP }}
                  >
                    <option value="faltante">faltante</option>
                    <option value="completo">completo</option>
                    <option value="por_vencer">por vencer</option>
                  </select>
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

function Nomina({ usuarios, esCelular }) {
  const [confirmar, setConfirmar] = useState(false);
  const card = { background: '#fff', borderRadius: 14, padding: 16, boxShadow: '0 2px 6px rgba(0,0,0,0.05)' };
  // placeholder
  const filas = usuarios.map(u => ({
    nombre: u.nombre,
    dias: 14, faltas: 0, deducciones: 200, total: 7800,
  }));
  const total = filas.reduce((s, f) => s + f.total, 0);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div style={{ ...card, background: '#eaf2fb' }}>
        <div style={{ fontSize: 11, color: COLOR_GRIS_CLARO, fontWeight: 700 }}>QUINCENA ACTUAL</div>
        <div style={{ fontSize: 24, fontWeight: 800, color: COLOR_OSCURO }}>${total.toLocaleString('es-MX', OPC_DINERO)}</div>
        <div style={{ fontSize: 11, color: COLOR_GRIS_CLARO }}>{filas.length} empleados</div>
      </div>
      <div style={{ ...card, padding: 0, overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
          <thead>
            <tr style={{ background: COLOR_FONDO, color: COLOR_GRIS_CLARO }}>
              <th style={{ padding: 10, textAlign: 'left' }}>Empleado</th>
              <th style={{ padding: 10 }}>Días</th>
              <th style={{ padding: 10 }}>Faltas</th>
              <th style={{ padding: 10 }}>Deducc.</th>
              <th style={{ padding: 10 }}>Total</th>
            </tr>
          </thead>
          <tbody>
            {filas.map((f, i) => (
              <tr key={i} style={{ borderTop: '1px solid #f0f2f5' }}>
                <td style={{ padding: 10, color: COLOR_GRIS, fontWeight: 600 }}>{f.nombre}</td>
                <td style={{ padding: 10, textAlign: 'center', color: COLOR_GRIS }}>{f.dias}</td>
                <td style={{ padding: 10, textAlign: 'center', color: COLOR_GRIS }}>{f.faltas}</td>
                <td style={{ padding: 10, textAlign: 'center', color: COLOR_GRIS }}>${Number(f.deducciones || 0).toLocaleString('es-MX', OPC_DINERO)}</td>
                <td style={{ padding: 10, textAlign: 'right', color: COLOR_OSCURO, fontWeight: 700 }}>${f.total.toLocaleString('es-MX', OPC_DINERO)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <button
        onClick={() => setConfirmar(true)}
        style={{ padding: 14, background: COLOR_PRINCIPAL, color: '#fff', border: 'none', borderRadius: 12, fontWeight: 700, fontSize: 14, cursor: 'pointer', fontFamily: PP }}
      >
        💵 Generar nómina
      </button>

      {confirmar && (
        <div style={overlay}>
          <div style={{ ...modalCard, maxWidth: 360, textAlign: 'center' }}>
            <div style={{ fontSize: 32, marginBottom: 8 }}>💵</div>
            <div style={{ fontSize: 16, fontWeight: 800, color: COLOR_OSCURO, marginBottom: 8 }}>Nómina generada</div>
            <div style={{ fontSize: 12, color: COLOR_GRIS_CLARO, marginBottom: 16 }}>Quincena por ${total.toLocaleString('es-MX', OPC_DINERO)} lista para revisión.</div>
            <button onClick={() => setConfirmar(false)} style={{ width: '100%', padding: 12, background: COLOR_PRINCIPAL, color: '#fff', border: 'none', borderRadius: 10, fontWeight: 700, cursor: 'pointer', fontFamily: PP }}>Entendido</button>
          </div>
        </div>
      )}
    </div>
  );
}

const overlay = { position: 'fixed', inset: 0, background: 'rgba(20,30,50,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16 };
const modalCard = { background: '#fff', borderRadius: 16, padding: 22, width: '100%', maxHeight: '92vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.25)', fontFamily: PP };

export default RecursosHumanos;
